/* Getting a reminder to a closed tab.
 *
 * This client had none of this. Not a partial implementation — nothing:
 * `grep -rn serviceWorker web/src` found no matches, so switching to it would
 * have turned off every push reminder for everybody, silently, while the app
 * looked completely fine. Reminders are the product's central promise, so that
 * would have been the worst possible way to ship a better-looking client.
 *
 * Ported from `prototype/net.js`, which learned four things the hard way. All
 * four are preserved here because the reasoning is what stops somebody
 * simplifying them back out:
 *
 *   1. **Ask on every sign-in, not only when permission is already granted.**
 *      The first version subscribed only if `Notification.permission` was
 *      already `granted` — and nothing ever asked for it. A new device signed
 *      in, was never prompted, never subscribed, and push silently never
 *      worked. It looked like a broken feature rather than a missing
 *      permission.
 *
 *   2. **Re-register the subscription every time**, not only when it is new.
 *      The server may have pruned an endpoint after a transient failure, and
 *      re-posting costs nothing.
 *
 *   3. **A subscription made against a different VAPID key can never be
 *      delivered to.** The server signs with a key the browser would reject.
 *      That happens whenever the server's keys are regenerated, and the only
 *      cure is to throw the old subscription away — so the keys are compared,
 *      byte for byte, rather than assumed.
 *
 *   4. **Never fail silently.** Every outcome sets a state the UI can show. The
 *      whole promise of the product is that it tells you things; a client that
 *      cannot register for notifications and says nothing is the one outcome
 *      to avoid.
 */

import { get, post } from '../api';

export type PushState =
  | 'unsupported'      // the browser has no push at all
  | 'sw-failed'        // the service worker would not register
  | 'default'          // nobody has been asked yet
  | 'denied'           // asked and refused; the browser will not ask again
  | 'granted'          // working
  | 'broken';          // allowed, and still could not subscribe — the silent one

export interface PushStatus { state: PushState; reason?: string }

let registration: ServiceWorkerRegistration | null = null;

const urlB64ToBytes = (b64: string): Uint8Array => {
  const pad = '='.repeat((4 - b64.length % 4) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
};

/** "Chrome on Windows" — so somebody can tell their devices apart in Settings. */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Macintosh/.test(ua) ? 'macOS'
    : /Windows/.test(ua) ? 'Windows'
    : /Linux/.test(ua) ? 'Linux' : 'device';
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  return `${browser} on ${os}`;
}

/** Hand the subscription to the server. Idempotent — keyed on the endpoint. */
async function registerSubscription(json: PushSubscriptionJSON): Promise<PushStatus> {
  if (!json?.endpoint) return { state: 'broken', reason: 'no endpoint' };
  const r = await post('/api/devices', {
    kind: 'web',
    label: deviceLabel(),
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth
  });
  return r.ok ? { state: 'granted' } : { state: 'broken', reason: r.error };
}

async function subscribe(): Promise<PushStatus> {
  if (!registration || !('PushManager' in window))
    return { state: 'unsupported' };

  const cfg = await get<{ vapidPublicKey?: string }>('/api/config');
  if (!cfg.ok || !cfg.vapidPublicKey)
    return { state: 'broken', reason: 'this server has no push keys configured' };

  try {
    let sub = await registration.pushManager.getSubscription();

    /* Rule 3. A subscription against a stale key is worse than none: it looks
       registered on both sides and nothing is ever delivered. */
    if (sub) {
      const mine = new Uint8Array(sub.options?.applicationServerKey ?? new ArrayBuffer(0));
      const want = urlB64ToBytes(cfg.vapidPublicKey);
      const same = mine.length === want.length && mine.every((b, i) => b === want[i]);
      if (!same) { await sub.unsubscribe().catch(() => {}); sub = null; }
    }

    if (!sub) {
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToBytes(cfg.vapidPublicKey)
      });
    }

    /* Rule 2: every time, not only on first subscribe. */
    return await registerSubscription(sub.toJSON());
  } catch (e) {
    return { state: 'broken', reason: (e as Error).message };
  }
}

/**
 * Register the worker. Safe to call more than once.
 *
 * `onReminder` is how a socket-delivered reminder and a push-delivered one end
 * up looking identical: the page forwards what arrives on the socket to the
 * worker, which shows it with the same tag, so whichever arrives first wins and
 * the second replaces it rather than double-notifying.
 */
export async function startWorker(
  onNotificationClick?: (payload: Record<string, unknown>, action: string) => void
): Promise<PushStatus> {
  if (!('serviceWorker' in navigator)) return { state: 'unsupported' };

  try {
    registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
  } catch (e) {
    console.warn('[push] service worker unavailable:', (e as Error).message);
    return { state: 'sw-failed', reason: (e as Error).message };
  }

  navigator.serviceWorker.addEventListener('message', ev => {
    /* The worker resubscribes when the browser rotates a subscription and
       hands the new one back here to register. */
    if (ev.data?.type === 'push.resubscribed') void registerSubscription(ev.data.subscription);
    if (ev.data?.type === 'reminder.clicked')
      onNotificationClick?.(ev.data.payload ?? {}, ev.data.action ?? '');
  });

  return ensure();
}

/**
 * Make this device's push state true, whatever it currently is.
 *
 * Called on every sign-in — rule 1. Returns what a person should be told.
 */
export async function ensure(): Promise<PushStatus> {
  if (!('Notification' in window) || !('PushManager' in window))
    return { state: 'unsupported' };

  if (Notification.permission === 'granted') {
    const r = await subscribe();
    /* Granted but unable to subscribe is the silent failure that matters: the
       person believes reminders are on, and nothing ever arrives. */
    return r.state === 'granted' ? r : { state: 'broken', reason: r.reason };
  }

  return { state: Notification.permission === 'denied' ? 'denied' : 'default' };
}

/**
 * Ask, then subscribe. **Must be called from a real user gesture** — asking
 * without one is blocked or quietly ignored by most browsers now, and a bare
 * permission dialog with no explanation gets refused anyway.
 */
export async function ask(): Promise<PushStatus> {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted')
      return { state: permission === 'denied' ? 'denied' : 'default' };
    return await subscribe();
  } catch (e) {
    return { state: 'broken', reason: (e as Error).message };
  }
}

/** Show a socket-delivered reminder through the worker, so both paths match. */
export function showThroughWorker(payload: unknown): boolean {
  if (!registration?.active) return false;
  registration.active.postMessage({ type: 'show', payload });
  return true;
}
